'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnchorHTMLAttributes, ReactNode } from 'react'

type Props = {
  href: string
  children: ReactNode
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'>

/**
 * Lien qui court-circuite le routeur pour les ancres de la page courante.
 *
 * `<Link href="/#contact">` ne déclenche aucune navigation quand l'URL
 * contient déjà `#contact` : après un rechargement puis un retour en
 * haut de page, recliquer sur « Contact » ne faisait plus rien. Le
 * routeur produisait en prime une URL `#contact#contact`.
 *
 * Sur la page cible, on rend une ancre native : le navigateur sait
 * défiler vers un fragment, y compris lorsqu'il est déjà dans l'URL.
 * Ailleurs, `next/link` reprend la main pour préserver la navigation
 * client.
 */
export function AnchorLink({ href, children, ...rest }: Props) {
  const pathname = usePathname()

  const hashIndex = href.indexOf('#')
  if (hashIndex !== -1) {
    const targetPath = href.slice(0, hashIndex) || pathname
    const hash = href.slice(hashIndex)

    if (targetPath === pathname) {
      return (
        <a href={hash} {...rest}>
          {children}
        </a>
      )
    }
  }

  return (
    <Link href={href} {...rest}>
      {children}
    </Link>
  )
}
